import { reactive } from 'vue';

/**
 * Lightweight i18n replacement for `vuex-i18n` (which has no Vue 3 release).
 *
 * It preserves the exact behaviour the app relies on:
 *   - `$t('key')`                       → looks the key up in the active locale
 *   - `$t('key', { subject: 'name' })`  → replaces `{subject}` style tokens
 *   - missing keys fall back to the key itself (vuex-i18n behaviour)
 *   - supports flat keys and dotted-nested keys
 *
 * State is reactive, so changing the locale or adding messages re-renders any
 * template that calls `$t(...)`.
 */
const state = reactive({
    locale: 'en',
    messages: {}, // { [locale]: { key: value } }
});

function resolve(dict, key) {
    if (dict == null) return undefined;
    if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
    if (key.indexOf('.') === -1) return undefined;
    return key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), dict);
}

function interpolate(str, replacements) {
    if (!replacements || typeof str !== 'string') return str;
    return str.replace(/\{(\w+)\}/g, (match, token) =>
        Object.prototype.hasOwnProperty.call(replacements, token) ? replacements[token] : match
    );
}

export function translate(key, replacements) {
    if (key === null || key === undefined) return key;
    const value = resolve(state.messages[state.locale], key);
    if (typeof value === 'string') {
        return interpolate(value, replacements);
    }
    // vuex-i18n falls back to the key when a translation is missing.
    return interpolate(key, replacements);
}

export const i18n = {
    add(locale, messages) {
        state.messages[locale] = Object.assign({}, state.messages[locale], messages || {});
    },
    set(locale) {
        state.locale = locale;
    },
    get locale() {
        return state.locale;
    },
    translate,
};

export default {
    install(app) {
        app.config.globalProperties.$t = translate;
        app.config.globalProperties.$i18n = i18n;
    },
};
